import React, { useState, useEffect, useCallback, useMemo } from "react";
import Logo from "../assets/sfsLogo2.png";
import { Phone, X, Menu } from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
// cSpell: ignore Subramaniam
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();
  const navigate = useNavigate();


  const navItems = useMemo(
    () => [
      { type: "page", path: "/about-us", label: "About Us" },
      { type: "section", id: "loan", label: "Loan" },
      { type: "section", id: "why-choose-us", label: "Why Choose Us" },
      { type: "section", id: "our-services", label: "Our Services" },
      { type: "page", path: "/contact-us", label: "Contact" },
    ],
    []
  );

  const updateHash = useCallback((id) => {
    const hash = `#${id}`;
    if (window.location.hash !== hash) {
      window.history.replaceState(null, "", hash);
    }
  }, []);


  useEffect(() => {
  let ticking = false;

  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 10);

        let current = "";
        navItems
          .filter((item) => item.type === "section")
          .forEach((item) => {
            const el = document.getElementById(item.id);
            if (el) {
              const offsetTop = el.offsetTop;
              if (window.scrollY >= offsetTop - 200) {
                current = item.id;
              }
            }
          });

        if (current && current !== activeSection) {
          setActiveSection(current);
          updateHash(current);
        }

        ticking = false;
      });

      ticking = true;
    }
  };

  if (location.pathname === "/") {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }
}, [activeSection, location.pathname, updateHash, navItems]);


  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.substring(1);
      scrollToSection(id);
      setActiveSection(id);
    }
  }, [location]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -100; // adjust for your header height
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleNavClick = (item) => {
    if (item.type === "section") {
      if (location.pathname !== "/") {
        navigate(`/#${item.id}`);
      } else {
        scrollToSection(item.id);
        window.history.replaceState(null, "", `#${item.id}`);
      }
      setActiveSection(item.id);
    } else if (item.type === "page") {
      navigate(item.path);
    }
    setIsOpen(false);
  };


  return (
    <header
      className={`bg-white w-full fixed top-0 z-50 transition-all duration-300 font-sans ${
        scrolled ? "shadow-lg" : "shadow-md"
      }`}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 md:h-24">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center lg:mr-5">
            <Link to="/">
              <img
                src={Logo}
                alt="Subramaniam Financial Services"
                className="cursor-pointer h-14 md:h-16 xl:h-20 w-auto transition-all duration-300 hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center bg-[#1e4d91] text-white px-6 py-2 rounded-xl font-medium">
            <div className="flex space-x-1">
              {navItems.map((item) => {
                const isActive =
                  location.pathname === "/"
                    ? activeSection === item.id
                    : location.pathname === item.path;

                return (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item)}
                    className={`px-4 py-2 rounded-md transition-all duration-300 ${
                      isActive
                        ? "text-white font-bold text-lg"
                        : "text-base hover:font-semibold hover:text-white"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Phone Number */}
          <div className="hidden lg:flex items-center ">
            <a
              href="tel:8973739377"
              className="running-border ml-6 inline-flex items-center bg-[#28a745] hover:bg-green-700 text-white px-5 py-3 rounded-md font-medium transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-4 h-4 mr-2 animate-ring" />
              <span className="text-sm">8973739377</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              aria-label="Menu"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 focus:outline-none transition-colors duration-300"
            >
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="lg:hidden bg-[#1e4d91] shadow-xl">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => {
              const isActive =
                location.pathname === "/"
                  ? activeSection === item.id
                  : location.pathname === item.path;

              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className={`block w-full text-left px-3 py-3 ${
                    isActive
                      ? "bg-white text-[#1e4d91] font-bold"
                      : "text-white"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
            <div className="pt-2">
              <a
                href="tel:8973739377"
                className="running-border w-full flex items-center justify-center bg-[#28a745] hover:bg-green-700 text-white px-4 py-3 rounded-md font-medium transition-colors duration-300"
              >
                <Phone className="w-4 h-4 mr-2 animate-ring" />
                <span>8973739377</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
